import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

import gypsumImg from "@/assets/services/gypsum-board.jpg";
import woodworkImg from "@/assets/services/custom-woodwork.jpg";
import pvcMarbleImg from "@/assets/services/pvc-marble.jpg";
import mirrorsImg from "@/assets/services/decorative-mirrors.jpg";
import shipboardImg from "@/assets/services/shipboard.jpg";
import stoneImg from "@/assets/services/stone-veneer.jpg";
import restorationImg from "@/assets/services/restoration.jpg";
import paintingImg from "@/assets/services/painting-wallpaper.jpg";

const services = [
  {
    title: "أعمال الجبس بورد",
    subtitle: "Gypsum Board Designs",
    desc: "تصاميم جبس بورد عصرية للأسقف والجدران — إضاءة مخفية، قواطع ديكورية، وأسقف معلّقة بأعلى معايير الجودة.",
    alt: "أعمال جبس بورد في الرياض",
    image: gypsumImg,
    slug: "gypsum-riyadh",
  },
  {
    title: "ديكورات وتصميم داخلي",
    subtitle: "Interior Design",
    desc: "تصاميم داخلية فاخرة للفلل والشقق والمكاتب — حلول إبداعية تعكس ذوقك بأعلى معايير الجودة.",
    alt: "ديكورات وتصميم داخلي في الرياض",
    image: woodworkImg,
    slug: "decorations-riyadh",
  },
  {
    title: "بديل الرخام وبديل الخشب",
    subtitle: "PVC Marble & WPC Louvers",
    desc: "ألواح بديل رخام وبديل خشب بتصاميم مودرن فاخرة — مقاومة للماء والحرارة، سهلة التركيب والصيانة.",
    alt: "تركيب بديل الخشب في الرياض",
    image: pvcMarbleImg,
    slug: "pvc-marble-riyadh",
  },
  {
    title: "تركيب مرايات ديكور",
    subtitle: "Decorative Mirrors",
    desc: "تركيب مرايات ديكورية بتصاميم حديثة — مرايات حائطية، مرايات مداخل، وتصاميم مخصصة لمساحات أنيقة.",
    alt: "تركيب مرايات ديكور في الرياض",
    image: mirrorsImg,
    slug: "mirrors-riyadh",
  },
  {
    title: "شيبورد",
    subtitle: "Shipboard",
    desc: "تركيب ألواح شيبورد عالية الجودة — حلول عملية وجمالية للجدران والأسقف بأسعار تنافسية.",
    alt: "تركيب شيبورد في الرياض",
    image: shipboardImg,
    slug: "shipboard-riyadh",
  },
  {
    title: "صفائح حجرية مودرن",
    subtitle: "Stone Veneer Sheets",
    desc: "صفائح حجرية رقيقة بتصاميم عصرية — لمسة فخامة طبيعية للجدران الداخلية والخارجية بوزن خفيف وتركيب سهل.",
    alt: "صفائح حجرية مودرن في الرياض",
    image: stoneImg,
    slug: "stone-veneer-riyadh",
  },
  {
    title: "دهانات وورق جدران",
    subtitle: "Painting & Wallpaper",
    desc: "دهانات احترافية بأفضل الماركات العالمية وتركيب ورق جدران بأرقى الخامات — نحوّل جدرانكم إلى لوحات فنية.",
    alt: "دهانات وورق جدران في الرياض",
    image: paintingImg,
    slug: "paint-riyadh",
  },
  {
    title: "ورق جدران فاخر",
    subtitle: "Luxury Wallpaper",
    desc: "ورق جدران ثلاثي الأبعاد وكلاسيك ومودرن بأحدث التصاميم العالمية وتركيب احترافي.",
    alt: "تركيب ورق جدران في الرياض",
    image: paintingImg,
    slug: "wallpaper-riyadh",
  },
  {
    title: "ترميم ومقاولات عامة",
    subtitle: "Restoration & Contracting",
    desc: "ترميم شامل للمباني والفلل — أعمال هيكلية وتشطيبات بإشراف هندسي متخصص وضمان على الجودة.",
    alt: "ترميم مباني ومقاولات عامة في الرياض",
    image: restorationImg,
    slug: "restoration-riyadh",
  },
  {
    title: "تركيب باركيه وأرضيات",
    subtitle: "Parquet & Flooring",
    desc: "تركيب باركيه وأرضيات خشبية وبديل خشب — أرضيات فاخرة بتشطيبات عالية الجودة وأسعار مناسبة.",
    alt: "تركيب باركيه وأرضيات في الرياض",
    image: shipboardImg,
    slug: "parquet-riyadh",
  },
];

const ServicesSection = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, direction: "rtl", align: "start", slidesToScroll: 1 },
    [Autoplay({ delay: 4000, stopOnInteraction: false, stopOnMouseEnter: true })]
  );
  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((i: number) => emblaApi?.scrollTo(i), [emblaApi]);

  return (
    <section id="services" className="section-padding bg-secondary text-secondary-foreground overflow-hidden">
      <div className="container mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <div className="w-12 h-0.5 gold-gradient mx-auto mb-4" />
          <h2 className="text-3xl md:text-5xl font-bold mb-4">خدماتنا المتميزة</h2>
          <p className="text-secondary-foreground/60 max-w-2xl mx-auto text-lg">
            حلول متكاملة للتصميم الداخلي والمقاولات في الرياض — جودة واحترافية تتجاوز التوقعات
          </p>
        </motion.div>

        {/* Carousel */}
        <div className="relative group/carousel">
          {/* Navigation Arrows */}
          <button
            onClick={scrollNext}
            className="absolute -right-2 md:right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 rounded-full bg-card/10 backdrop-blur-sm border border-border/20 flex items-center justify-center text-secondary-foreground/60 hover:text-primary hover:border-primary/40 transition-all duration-300 opacity-0 group-hover/carousel:opacity-100"
            aria-label="الخدمة التالية"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          <button
            onClick={scrollPrev}
            className="absolute -left-2 md:left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 rounded-full bg-card/10 backdrop-blur-sm border border-border/20 flex items-center justify-center text-secondary-foreground/60 hover:text-primary hover:border-primary/40 transition-all duration-300 opacity-0 group-hover/carousel:opacity-100"
            aria-label="الخدمة السابقة"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <div ref={emblaRef} className="overflow-hidden mx-4 md:mx-10">
            <div className="flex">
              {services.map((service, i) => (
                <div
                  key={service.title}
                  className="flex-[0_0_100%] sm:flex-[0_0_50%] lg:flex-[0_0_33.333%] min-w-0 pl-4 md:pl-6"
                  role="group"
                  aria-roledescription="slide"
                  aria-label={service.alt}
                >
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.05 }}
                    className="h-full"
                  >
                    <Link
                      to={`/services/${service.slug}`}
                      className="group relative h-full rounded-2xl overflow-hidden bg-card/5 border border-border/10 hover:border-primary/30 hover:shadow-lg hover:shadow-primary/10 transition-all duration-500 block cursor-pointer"
                    >
                      {/* Image */}
                      <div className="relative h-52 md:h-56 overflow-hidden">
                        <img
                          src={service.image}
                          alt={service.alt}
                          loading="lazy"
                          width={800}
                          height={600}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-secondary/90 via-secondary/20 to-transparent" />
                        <span className="absolute bottom-3 right-4 text-xs font-medium text-primary/90 tracking-wide">{service.subtitle}</span>
                      </div>

                      {/* Content */}
                      <div className="p-5 md:p-6">
                        <h3 className="text-lg font-bold text-secondary-foreground mb-2">{service.title}</h3>
                        <p className="text-secondary-foreground/50 text-sm leading-relaxed mb-4">{service.desc}</p>
                        <span className="inline-flex items-center gap-2 text-sm font-semibold text-primary group-hover:text-primary/80 transition-colors">
                          تفاصيل الخدمة
                          <ChevronLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
                        </span>
                      </div>
                    </Link>
                  </motion.div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Pagination Dots */}
        <div className="flex justify-center gap-2 mt-8">
          {services.map((_, i) => (
            <button
              key={i}
              onClick={() => scrollTo(i)}
              className={`h-2 rounded-full transition-all duration-300 ${
                selectedIndex === i
                  ? "w-8 gold-gradient"
                  : "w-2 bg-secondary-foreground/20 hover:bg-secondary-foreground/40"
              }`}
              aria-label={`انتقل للخدمة ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
